import React from 'react';
import axios from 'axios';
import { Modal } from 'antd';
import WarningOutlined from '@ant-design/icons/WarningOutlined';
import { NOT_SHOW_MAINTEANCE_REMINDER, TYPE_COOKIE } from './constants';
import { getCookie } from './cookies';
import { nanoid } from '@reduxjs/toolkit';
import { notify, Status } from 'reapop';
import { store } from '../';
import { AppState } from 'app/pages/App/slice/types';
import { appActions } from 'app/pages/App/slice';
import DOMPurify from 'dompurify';
import { formatDate, getDateStringForValidDate } from './time';
import moment from 'moment';
import { DateFromToUtils } from 'utils/date-utils/date-utils';
import { DateFormat } from 'utils/date-utils/date-format';

const { confirm } = Modal;

export const openNotificationWithIcon = (
  type: Status,
  message: string,
  description?: string,
  duration?: number,
) => {
  store.dispatch(
    notify(escapeHTML(description || ''), type, {
      title: message || 'Notification',
      dismissAfter: (duration || 3) * 1000,
    }),
  );
};

export const openMessageWithIcon = (
  type: Status,
  content: any,
  duration?: number,
) => {
  store.dispatch(
    notify(escapeHTML(content || ''), type, {
      dismissAfter: (duration || 3) * 1000,
    }),
  );
  // key && message.config({ key });
};

export const openConfirmModal = (
  onOk: () => void,
  title: string = 'Notification',
  description: string = 'Are you sure?',
  onCancel: () => void = () => {},
  okButtonText: string = 'Submit',
  cancelButtonText: string = 'Cancel',
) => {
  confirm({
    title: title,
    icon: <WarningOutlined />,
    content: description,
    okText: okButtonText,
    cancelText: cancelButtonText,
    onOk: () => onOk(),
    onCancel: () => onCancel(),
    maskTransitionName: '',
    transitionName: '',
  });
};
/**
 *
 * @param object
 * @returns new object with no property undefined or null
 */
export const cleanObject = <T extends Object>(object: T) => {
  const newObj: T = Object.assign({}, object);
  Object.keys(newObj).forEach((key: string) => {
    if (newObj[key] === null || newObj[key] === undefined) delete newObj[key];
  });
  return newObj;
};

export const auth = () => !!getCookie(TYPE_COOKIE.TOKEN);

/**
 * @param {number} input: number
 * @return number formatted, 100000 => 100,000
 */
export const formatNumber = (number: number, separator: string = ',') => {
  if (number === 0 || number === null || number === undefined) return '0';
  const value = number + '';
  const list = value.split('.');
  const prefix = list[0].charAt(0) === '-' ? '-' : '';
  let num = prefix ? list[0].slice(1) : list[0];
  let result = '';
  while (num.length > 3) {
    result = `${separator}${num.slice(-3)}${result}`;
    num = num.slice(0, num.length - 3);
  }
  if (num) {
    result = num + result;
  }
  return `${prefix}${result}`;
};

/**
 * @param func function debounced
 * @param waitFor time debounce
 */
export const debounce = <F extends (...args: any) => any>(
  func: F,
  waitFor: number,
) => {
  let timeout: number = 0;

  const debounced = (...args: any) => {
    clearTimeout(timeout);
    setTimeout(() => func(...args), waitFor);
  };

  return debounced as (...args: Parameters<F>) => ReturnType<F>;
};

/**
 * @return id random
 */
export const generateRandomID = () => nanoid();

/**
 *
 * @param length
 * @returns: string random has length
 */
export function generateStringRandom(length: number): string {
  var result = '';
  var characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  var charactersLength = characters.length;
  for (var i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}

export const addScriptDynamic = (src, onload?: () => void) => {
  let script = document.createElement('script');
  script.setAttribute('src', src);
  if (onload) script.onload = onload;
  document.head.appendChild(script);
};

export const toggleItemArrs = <T extends string | number>(
  arrs: T[],
  item: T,
) => {
  const listXor: T[] = arrs.some(el => el === item)
    ? arrs.filter(el => el !== item)
    : [...arrs, item];
  return listXor;
};

/**
 *
 * @param {*} arr1: []
 * @param {*} arr2" []
 * @param {*} field: indicated whether array of object or array of item
 * @returns list item difference arr1 compared to arr2
 */
export const differenceArrays = (arr1, arr2, field = 'id') =>
  arr1.filter(
    current =>
      !arr2.some(el => (field ? el[field] === current[field] : el === current)),
  );

export const onDisabledReactDevtoolOnProduction = () => {
  if (process.env.NODE_ENV === 'production') {
    // Ensure the React Developer Tools global hook exists
    const _window = window as any;
    if (typeof _window.__REACT_DEVTOOLS_GLOBAL_HOOK__ !== 'object') return;

    // Replace all global hook properties with a no-op function or a null value
    Object.keys(_window.__REACT_DEVTOOLS_GLOBAL_HOOK__).forEach(prop => {
      if (prop === 'renderers') {
        // prevents console error when dev tools try to iterate of renderers
        _window.__REACT_DEVTOOLS_GLOBAL_HOOK__[prop] = new Map();
      } else {
        _window.__REACT_DEVTOOLS_GLOBAL_HOOK__[prop] =
          typeof _window.__REACT_DEVTOOLS_GLOBAL_HOOK__[prop] === 'function'
            ? Function.prototype
            : null;
      }
    });
  }
};

export const windowOpenLink = ({
  url,
  target,
  features,
  notShowMaintenanceReminder = true,
}: {
  url?: string | URL;
  target?: string;
  features?: string;
  notShowMaintenanceReminder?: boolean;
}) => {
  if (notShowMaintenanceReminder)
    sessionStorage.setItem(
      NOT_SHOW_MAINTEANCE_REMINDER,
      NOT_SHOW_MAINTEANCE_REMINDER,
    );
  window.open(url, target, features);
};

export const onFormatMoney = (money: number) =>
  money.toLocaleString('it-IT', { style: 'currency', currency: 'VND' });

export const removeHTML = (str: string) => {
  let result = str || '';
  if (str) {
    result = result.replace(/\n/g, '<br />').replace(/<\/?[^>]+(>|$)/g, ' ');
  }
  return result;
};

export const onFuncCheckShowDialogLogin = () => {
  return new Promise((resolve, reject) => {
    const state = store.getState();
    const stateApp: unknown = state.app;
    if (stateApp) {
      if ((stateApp as AppState)?.isLogged) {
        return resolve(true);
      } else {
        store.dispatch(appActions.openDialogLogin());
        return reject(false);
      }
    }
  });
};

export const onScrollTo = (container: HTMLElement, offset = -20) => {
  const yOffset = offset;
  const y =
    container.getBoundingClientRect().top + window.pageYOffset + yOffset;
  window.scrollTo({ top: y, behavior: 'smooth' });
};

const escape = document.createElement('textarea');
export function escapeHTML(html) {
  escape.textContent = html;
  return escape.innerHTML;
}

export function unescapeHTML(html) {
  escape.innerHTML = html;
  return escape.textContent;
}

export function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    var r = (Math.random() * 16) | 0,
      v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function purifyHtml(html) {
  const sanitizedHTML = DOMPurify.sanitize(html);
  return sanitizedHTML;
}

const enum platform {
  WindowsPhone = 'Windows Phone',
  Android = 'Android',
  iOS = 'iOS',
}

/**
 * Determine the mobile operating system.
 * This function returns one of 'iOS', 'Android', 'Windows Phone', or 'Web'.
 *
 * @returns {String}
 */
function getMobileOperatingSystem() {
  var userAgent = navigator.userAgent;
  if (/windows phone/i.test(userAgent)) {
    return 'Windows Phone';
  }
  if (/android/i.test(userAgent)) {
    return 'Android';
  }
  if (/iPad|iPhone|iPod/.test(userAgent)) {
    return 'iOS';
  }
  return 'Web';
}

const platformOS = getMobileOperatingSystem();
export const isAndroid = platformOS === platform.Android;

export const getFromDate = (
  fromDateInp: moment.Moment,
  toDateInp: moment.Moment,
  numberAddDay: number,
) => {
  return getDateStringForValidDate(
    fromDateInp.format('DD/MM/YYYY'),
    toDateInp.format('DD/MM/YYYY'),
    numberAddDay,
    (fromDate, toDate) => {
      const fromDateString = fromDate.toString();
      const toDateString = toDate.toString();
      if (fromDateString === toDateString) {
        return fromDateString;
      }
      return `${fromDateString} - ${toDateString}`;
    },
  );
};

export const getCalDate = (
  fromDate: string,
  toDate: string,
  numberDateAddToFrom?: number,
) => {
  const standardDayAdd = Number(numberDateAddToFrom);
  const addDay = standardDayAdd <= 0 ? 1 : standardDayAdd;
  const fromDateMoment = moment(fromDate, 'DD/MM/YYYY');
  const toDateByAddDayMoment = moment(fromDate, 'DD/MM/YYYY').add(
    addDay - 1,
    'days',
  );
  const toDateMoment = moment(toDate, 'DD/MM/YYYY');

  const standardToDateMoment = toDateByAddDayMoment.isBefore(toDateMoment)
    ? toDateByAddDayMoment
    : toDateMoment;
  const newFromDate = fromDateMoment.format('DD/MM/YYYY');
  const newToDate = standardToDateMoment.format('DD/MM/YYYY');

  if (
    fromDateMoment.format('DD/MM/YYYY') ===
    standardToDateMoment.format('DD/MM/YYYY')
  ) {
    return {
      fromDate: newFromDate !== 'Invalid date' ? newFromDate : '',
      toDate: newFromDate !== 'Invalid date' ? newFromDate : '',
    };
  }
  return {
    fromDate: newFromDate !== 'Invalid date' ? newFromDate : '',
    toDate: newToDate !== 'Invalid date' ? newToDate : '',
  };
};

const getDate = (fromDate: string, toDate: string) => {
  if (fromDate === toDate) {
    return {
      fromDate,
      toDate: fromDate,
    };
  }
  return {
    fromDate,
    toDate,
  };
};

export const getFromDateToDate = value => {
  const result = new DateFromToUtils.Builder('en', 'DD/MM/YYYY')
    .setFromDate(
      value?.ticket?.vgsUsageDate
        ? DateFormat.create(
            'YYYY-MM-DD HH:mm',
            value?.ticket?.vgsUsageDate,
          ).getMoment()
        : DateFormat.create('DD/MM/YYYY', value?.date?.dateDMY).getMoment(),
    )
    .setToDate(
      DateFormat.create(
        'YYYY-MM-DD HH:mm',
        value?.ticket?.vgsUsageDateTo,
      ).getMoment(),
    )
    .build()
    .getDateFromTo();
  // const isComboTicket = value?.catalog?.mapVGS === 'S4';
  // const useDateFrom = value?.date?.dateDMY;
  // const useDate = value?.show?.dateTo?.dateDMY;
  // const selectedDate = value?.selectedDate?.dateDMY;
  return { fromDate: result.fromDateString, toDate: result.toDateString };
};

export function findIntersection(...arrays: string[][]): string[] {
  if (arrays.length === 0) return [];

  return arrays.reduce((acc, array) => {
    const set = new Set(array);
    return acc.filter(value => set.has(value));
  });
}

/**
 * Format date: xx:xx-xx:xx
 *
 * @returns {String}
 */
export function formatTimeFrame(string) {
  if (!string) return '';
  return string.replace(/(\d{2}:\d{2})\s*-\s*(\d{2}:\d{2})/, '$1-$2');
}

type ObjectType = Record<string, any>;

export function addTicketType<T extends ObjectType | FormData>(input?: T): T {
  let keySession = 'ticketType';
  const forceTicketType: string | null =
    (input instanceof FormData
      ? input.get('forceTicketType')
      : ((input || {}) as ObjectType).forceTicketType) ?? null;

  if (forceTicketType) {
    if (input instanceof FormData) {
      if (input.has('ticketType')) {
        input.delete('ticketType');
      }
      input.append('ticketType', forceTicketType);
      input.delete('forceTicketType');
      return input;
    }

    delete (input as ObjectType).forceTicketType;
    return {
      ...(input as ObjectType),
      ticketType: forceTicketType,
    } as unknown as T;
  }

  const ticketType: string | null = sessionStorage.getItem(keySession) ?? null;

  if (!ticketType) return (input || {}) as T;

  if (input instanceof FormData) {
    if (input.has('ticketType')) {
      input.delete('ticketType');
    }
    input.append('ticketType', ticketType);
    return input;
  }

  return { ...(input as ObjectType), ticketType } as unknown as T;
}

export const clickToDownload = (url: string, fileName?: string) => {
  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  link.download = fileName || url;

  document.body.appendChild(link);

  link.click();

  document.body.removeChild(link);
};

/**
 * Tải file từ link BE trả về (đã mã hóa, đi qua APIM).
 * Không mở link trực tiếp được vì APIM yêu cầu apim-sub-key — phải fetch kèm
 * header rồi tải blob. Tên file lấy từ Content-Disposition nếu không truyền vào.
 */
export const downloadSignedFile = async (url?: string, fileName?: string) => {
  if (!url) return;
  const AIPM_TOKEN = process.env.REACT_APP_AIPM_TOKEN;
  const res = await axios.get(url, {
    responseType: 'blob',
    headers: {
      ...(AIPM_TOKEN ? { 'apim-sub-key': `${AIPM_TOKEN}` } : {}),
    },
  });
  const disposition =
    (res.headers && (res.headers['content-disposition'] as string)) || '';
  const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  const name =
    fileName || (match ? decodeURIComponent(match[1]) : 'download.xlsx');
  const blob = new Blob([res.data]);
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.style.display = 'none';
  link.href = objectUrl;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(objectUrl);
};
export const objectToSearchParams = (obj: Record<string, any>) =>
  Object.entries(obj)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join('&');
type ReplaceObject = {
  value: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
};
type ReplaceValue = React.ReactNode | ReplaceObject;
type Replacements = Record<string, ReplaceValue>;
const escapeRegExp = (str: string) =>
  str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
// ✅ type guard
const isReplaceObject = (val: ReplaceValue): val is ReplaceObject => {
  return (
    typeof val === 'object' &&
    val !== null &&
    !React.isValidElement(val) &&
    'value' in val
  );
};
export const renderReplaceText = (
  text: string,
  replacements?: Replacements,
): React.ReactNode => {
  if (!text || !replacements) return text;
  const keys = Object.keys(replacements);
  if (!keys.length) return text;
  const isAllPrimitive = keys.every(key => {
    const val = replacements[key];
    return !isReplaceObject(val);
  });
  if (isAllPrimitive) {
    let result = text;
    for (const key of keys) {
      const value = replacements[key];
      const replaceValue =
        typeof value === 'string' || typeof value === 'number'
          ? String(value)
          : '';
      result = result.replaceAll(`\${${key}}`, replaceValue);
    }
    return result;
  }
  const regex = new RegExp(
    keys.map(k => escapeRegExp(`\${${k}}`)).join('|'),
    'g',
  );
  const result: React.ReactNode[] = [];
  let lastIndex = 0;
  for (const match of text.matchAll(regex)) {
    const matchText = match[0];
    const key = matchText.slice(2, -1);
    const index = match.index ?? 0;
    if (lastIndex < index) {
      result.push(text.slice(lastIndex, index));
    }
    const replaceItem = replacements[key];
    if (replaceItem !== undefined) {
      if (isReplaceObject(replaceItem)) {
        result.push(
          <span
            key={`${key}-${index}`}
            className={replaceItem.className}
            style={replaceItem.style}
          >
            {replaceItem.value}
          </span>,
        );
      } else {
        result.push(replaceItem);
      }
    }
    lastIndex = index + matchText.length;
  }
  if (lastIndex < text.length) {
    result.push(text.slice(lastIndex));
  }
  return <>{result}</>;
};
/**
 * Format airport information to display as: name (code), country
 * @param airport - Airport object with code, name, and optionally countryName
 * @param options - Formatting options
 * @returns Formatted airport string
 * @example
 * formatAirport({ name: 'Tan Son Nhat', code: 'SGN', countryName: 'Vietnam' })
 * // Returns: "Tan Son Nhat (SGN), Vietnam"
 */
export interface FormatAirportOptions {
  showCountry?: boolean;
}

export interface AirportInfo {
  code?: string;
  name?: string;
  cityName?: string;
  countryName?: string;
}

export const formatAirport = (
  airport?: AirportInfo | string | null,
  options: FormatAirportOptions = {},
): string => {
  if (!airport) return '';

  // If airport is a string (airport code), return it as is
  if (typeof airport === 'string') return airport;

  const { code, name, cityName, countryName } = airport;
  const { showCountry = true } = options;

  // Prefer name (airport name) over cityName for display
  const displayName = name || cityName;

  // Build main part: name (code) - tên sân bay trước, mã code trong ngoặc
  const main =
    code && displayName
      ? `${displayName} (${code})`
      : displayName
      ? displayName
      : code || '';

  if (!main) return '';

  // Append country if available and showCountry is true
  return showCountry && countryName ? `${main}, ${countryName}` : main;
};
